/**
 * RBAC-3.3 · FR-2, FR-2d. Duplicate a role, with the reserved permissions dropped and named.
 *
 * ⚠️ THE DATABASE IS THE ENFORCEMENT; THIS IS THE WORDING.
 *
 * `20261101a2_rbac_3_3_duplicate_role.sql` does the stripping and the refusing:
 *   - `role_duplication_preview(source)` says what a copy would carry and what it would drop;
 *   - `duplicate_role(source, name, description, acknowledged_dropped)` refuses with 22023 unless
 *     `acknowledged_dropped` is exactly the set it is about to drop.
 *
 * So FR-2d ("names every permission it did not copy, before it copies") holds even for a client
 * that skips this module: there is no way to make a copy without first having been told. This
 * module's job is that the telling reads as a sentence, and that the acknowledgement it sends is the
 * list the admin was actually shown (`duplicateRoleArgs`), never a fresh read.
 *
 * ⚠️ There is no un-duplicable role. Never hide or disable Duplicate because a role carries a
 * reserved permission: Admin carries `manage_settings`, and RBAC-3.4 (Adopt) depends on it.
 */

import { permissionRow } from './permissionDisplay';

export const ROLE_DUPLICATION_PREVIEW_RPC = 'role_duplication_preview';
export const DUPLICATE_ROLE_RPC = 'duplicate_role';

export interface RoleDuplicationPreview {
  sourceRoleId: number;
  sourceName: string;
  sourceGlobal: boolean;
  suggestedName: string;
  /** Permission keys the copy will carry. */
  copied: string[];
  copiedCount: number;
  /** Reserved permission keys the copy will NOT carry, sorted, as the server compares them. */
  dropped: string[];
}

export interface DuplicateRoleResult {
  roleId: number;
  name: string;
  sourceRoleId: number;
  copiedCount: number;
  dropped: string[];
}

export interface DroppedPermissionLine {
  key: string;
  label: string;
  description: string;
}

export interface DuplicateConfirmationCopy {
  title: string;
  summary: string;
  dropped: DroppedPermissionLine[];
  note: string;
  confirmLabel: string;
}

/** The PostgREST error fields this module reads. */
export interface RpcErrorLike {
  code?: string | null;
  message?: string | null;
  hint?: string | null;
}

const isStringArray = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === 'string');

export function parseDuplicationPreview(raw: unknown): RoleDuplicationPreview | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  // A missing dropped list must never read as "nothing dropped".
  if (typeof r.source_role_id !== 'number' || typeof r.source_name !== 'string') return null;
  if (typeof r.copied_count !== 'number' || !isStringArray(r.dropped)) return null;
  return {
    sourceRoleId: r.source_role_id,
    sourceName: r.source_name,
    sourceGlobal: r.source_global === true,
    suggestedName: typeof r.suggested_name === 'string' ? r.suggested_name : `${r.source_name} (copy)`,
    copied: isStringArray(r.copied) ? r.copied : [],
    copiedCount: r.copied_count,
    dropped: r.dropped,
  };
}

export function parseDuplicateResult(raw: unknown): DuplicateRoleResult | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.role_id !== 'number' || typeof r.name !== 'string') return null;
  return {
    roleId: r.role_id,
    name: r.name,
    sourceRoleId: typeof r.source_role_id === 'number' ? r.source_role_id : 0,
    copiedCount: typeof r.copied_count === 'number' ? r.copied_count : 0,
    dropped: isStringArray(r.dropped) ? r.dropped : [],
  };
}

/**
 * Each dropped permission by its display label (RBAC-2.2), never by key. A key the display layer
 * does not know is still named, by its key: omitting it would break FR-2d, which is worse than an
 * unfriendly label.
 */
export function droppedPermissionLines(dropped: readonly string[]): DroppedPermissionLine[] {
  return dropped.map((key) => {
    const row = permissionRow(key);
    return row ? { key, label: row.label, description: row.description } : { key, label: key, description: '' };
  });
}

export function confirmationCopy(preview: RoleDuplicationPreview): DuplicateConfirmationCopy {
  const n = preview.dropped.length;
  const note = 'The copy belongs to your account. Nobody is moved onto it until you assign it.';
  if (n === 0) {
    return {
      title: `Duplicate ${preview.sourceName}`,
      summary: `Your copy will have all ${preview.copiedCount} permissions of ${preview.sourceName}.`,
      dropped: [],
      note,
      confirmLabel: 'Duplicate',
    };
  }
  return {
    title: `Duplicate ${preview.sourceName}`,
    summary:
      `Your copy will have ${preview.copiedCount} permissions. ${n} will not be copied, ` +
      `because only Joules can grant ${n === 1 ? 'it' : 'them'}:`,
    dropped: droppedPermissionLines(preview.dropped),
    note,
    confirmLabel: n === 1 ? 'Duplicate without this 1' : `Duplicate without these ${n}`,
  };
}

/**
 * The RPC arguments. The acknowledgement is the preview's own `dropped`, i.e. the list the admin
 * was shown, so if anything changed in between the server refuses rather than copying quietly.
 */
export function duplicateRoleArgs(
  preview: RoleDuplicationPreview,
  name: string | null | undefined,
  description: string | null | undefined,
) {
  const n = (name ?? '').trim();
  const d = (description ?? '').trim();
  return {
    p_source_role_id: preview.sourceRoleId,
    p_name: n === '' ? null : n,
    p_description: d === '' ? null : d,
    p_acknowledged_dropped: [...preview.dropped],
  };
}

/** 22023: the dropped set changed since the preview (or none was sent). Re-preview and re-confirm. */
export function isStaleAcknowledgement(err: RpcErrorLike | null | undefined): boolean {
  return err?.code === '22023';
}

/** The server's sentences are written for the admin; keep them, and add the hint. */
export function duplicateRoleErrorMessage(err: RpcErrorLike | null | undefined): string {
  const message = err?.message?.trim();
  if (!message) return 'The role could not be duplicated.';
  const hint = err?.hint?.trim();
  return hint ? `${message} ${hint}` : message;
}
